// Unpublished Work © 2022-2026 Deere & Company.

import {deleteEquipmentExpense, getEquipmentExpenses} from 'Services/equipment-service';
import {dateCompare} from 'Ui/models/maintenance';
import {formatDateOrDefault} from 'OnEquip/equipment/utils/equipment-detail-formatters';
import {EXPENSE_TYPES as equipmentExpenseTypes} from 'Ui/constants/equipment-expense-types-constants';
import {capitalizeFirstLetter} from 'Utils/translation-utils';
import EditLink from 'Ui/components/common/data-table/edit-link';
import React from 'react';
import {IconDelete} from '@deere/icons';
import OnLinkIconButton from 'Ui/components/common/onlink-icon-button';
import {getDeleteEntryConfirmationMessage} from 'Utils/data-utils';
import {getFormattedCurrency} from 'Utils/unit-conversion-utils';

const ICON_STYLE = {
    style: {
        height: '20px',
        width: '20px'
    }
};

export async function getRowData(equipment, translations, currencyPreference) {
    if (equipment?.equipmentId) {
        const {equipmentExpenses} = await getEquipmentExpenses(equipment.equipmentId);

        return equipmentExpenses?.map((equipmentExpense) => {
            const expenseType = equipmentExpenseTypes[equipmentExpense.expenseType];

            return {
                ...equipmentExpense,
                formattedDateSelect: formatDateOrDefault(equipmentExpense.dateSelect),
                expenseType: capitalizeFirstLetter(translations[expenseType] || expenseType),
                formattedAmount: getFormattedCurrency(equipmentExpense.amount, {
                    currencyPreference,
                    maximumFractionDigits: 2
                })
            };
        }) ?? [];
    }

    return [];
}

export function getColumns(translations, openAddEditExpenseDialog, openConfirmation, onCancel,
    updateEquipment, isFleetToMaintenanceEnabled) {
    return [
        {
            headerName: translations.DATE,
            field: 'formattedDateSelect',
            minWidth: 150,
            flex: 1,
            valueGetter: (_value, row) => row.dateSelect,
            sortComparator: dateCompare,
            renderCell: (params) => {
                if (isFleetToMaintenanceEnabled) {
                    return params.row.formattedDateSelect;
                }
                return (<EditLink onClick={openAddEditExpenseDialog(params.row.equipmentExpenseId)}>
                    {params.row.formattedDateSelect}
                </EditLink>);
            }
        },
        {
            headerName: translations.TYPE,
            minWidth: 150,
            flex: 1,
            field: 'expenseType'
        },
        {
            headerName: translations.ONLINK_AMOUNT,
            field: 'amount',
            type: 'number',
            minWidth: 120,
            flex: 1,
            renderCell: (params) => {
                return params.row.formattedAmount;
            }
        },
        !isFleetToMaintenanceEnabled && {
            field: 'delete',
            className: 'delete-col',
            headerClassName: 'delete-col',
            headerName: '',
            sortable: false,
            width: 30,
            renderCell: (params) => {
                return (
                    <OnLinkIconButton
                        onClick={() => openConfirmation({
                            translations,
                            showLoadingIcon: true,
                            message: getDeleteEntryConfirmationMessage(translations),
                            onContinue: async () => {
                                await deleteEquipmentExpense(params.row.equipmentExpenseId);
                                onCancel();
                                updateEquipment();
                            },
                            title: translations.ONLINK_DELETE_ENTRY,
                            onCancel
                        })}
                    >
                        <IconDelete
                            {...ICON_STYLE}
                        />
                    </OnLinkIconButton>
                );
            }
        }
    ].filter(Boolean);
}
